<script setup>
import { ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useFetch } from '@/plugins/fetch';

const emit = defineEmits(['close']);
const fetch = useFetch();
const toast = useToast();

const step = ref('intro'); // intro, confirm, display
const password = ref('');
const isLoading = ref(false);
const recoveryCodes = ref([]);
const copiedIndex = ref(null);
const hasExistingCodes = ref(false);
const savedConfirmation = ref(false);

// Check if user already has recovery codes
const checkExistingCodes = async () => {
  try {
    const status = await fetch('auth/2fa/recovery/status');
    hasExistingCodes.value = status.has_codes;
  } catch {
    hasExistingCodes.value = false;
  }
};

const generateCodes = async () => {
  if (!password.value) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please enter your password',
      life: 3000,
    });
    return;
  }

  isLoading.value = true;
  try {
    const response = await fetch('auth/2fa/recovery/generate', {
      method: 'POST',
      body: { password: password.value },
    });
    // Handle wrapped API response
    const codes = response?.data?.codes || response?.codes || response?.data || [];
    recoveryCodes.value = Array.isArray(codes) ? codes : [];
    if (recoveryCodes.value.length === 0) {
      throw new Error('No recovery codes received from server');
    }
    step.value = 'display';
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error?.message || 'Failed to generate recovery codes',
      life: 3000,
    });
  } finally {
    isLoading.value = false;
  }
};

const copyCode = (code, index) => {
  navigator.clipboard.writeText(code);
  copiedIndex.value = index;
  setTimeout(() => {
    copiedIndex.value = null;
  }, 2000);
};

const copyAllCodes = () => {
  const codesText = recoveryCodes.value.join('\n');
  navigator.clipboard.writeText(codesText);
  toast.add({
    severity: 'success',
    summary: 'Copied',
    detail: 'All recovery codes copied to clipboard',
    life: 3000,
  });
};

const downloadCodes = () => {
  if (!recoveryCodes.value || recoveryCodes.value.length === 0) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No recovery codes to download',
      life: 3000,
    });
    return;
  }
  const codesText = `Calid Recovery Codes
Generated: ${new Date().toISOString()}

IMPORTANT: Keep these codes in a safe place.
Each code can only be used once.

${recoveryCodes.value.map((code, i) => `${i + 1}. ${code}`).join('\n')}
`;
  const blob = new Blob([codesText], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'calid-recovery-codes.txt';
  a.click();
  URL.revokeObjectURL(url);
};

const printCodes = () => {
  const printWindow = window.open('', '_blank');
  printWindow.document.write(`
    <html>
      <head>
        <title>Recovery Codes</title>
        <style>
          body { font-family: monospace; padding: 2rem; }
          h1 { font-size: 1.5rem; }
          .codes { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; margin: 1rem 0; }
          .code { padding: 0.5rem; background: #f5f5f5; border-radius: 4px; }
          .warning { color: #dc2626; margin-top: 1rem; }
        </style>
      </head>
      <body>
        <h1>Calid Recovery Codes</h1>
        <p>Generated: ${new Date().toLocaleDateString()}</p>
        <div class="codes">
          ${recoveryCodes.value.map((code) => `<div class="code">${code}</div>`).join('')}
        </div>
        <p class="warning">⚠️ Each code can only be used once. Store safely!</p>
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.print();
};

checkExistingCodes();
</script>

<template>
  <div class="recovery-codes">
    <!-- Intro Step -->
    <div v-if="step === 'intro'" class="intro-step">
      <div class="intro-icon">
        <i class="fas fa-key"></i>
      </div>
      <h5>Backup Recovery Codes</h5>
      <p>
        Recovery codes can be used to access your account if you lose access to your
        authenticator device or phone number.
      </p>

      <div v-if="hasExistingCodes" class="existing-warning">
        <i class="fas fa-exclamation-triangle"></i>
        <span>You already have recovery codes. Generating new codes will invalidate the old ones.</span>
      </div>

      <div class="features">
        <div class="feature">
          <i class="fas fa-check-circle"></i>
          <span>8 single-use codes</span>
        </div>
        <div class="feature">
          <i class="fas fa-check-circle"></i>
          <span>Works offline</span>
        </div>
        <div class="feature">
          <i class="fas fa-check-circle"></i>
          <span>Emergency access</span>
        </div>
      </div>

      <button class="btn-generate" @click="step = 'confirm'">
        {{ hasExistingCodes ? 'Regenerate Codes' : 'Generate Codes' }}
      </button>
    </div>

    <!-- Confirm Step -->
    <div v-if="step === 'confirm'" class="confirm-step">
      <div class="confirm-icon">
        <i class="fas fa-lock"></i>
      </div>
      <h5>Confirm Your Identity</h5>
      <p>Enter your password to generate recovery codes.</p>

      <div class="password-field">
        <label>Password</label>
        <input
          v-model="password"
          type="password"
          placeholder="Enter your password"
          @keyup.enter="generateCodes"
          autofocus
        />
      </div>

      <div class="confirm-actions">
        <button class="btn-back" @click="step = 'intro'">
          <i class="fas fa-arrow-left"></i>
          Back
        </button>
        <button
          class="btn-confirm"
          @click="generateCodes"
          :disabled="!password || isLoading"
        >
          <span v-if="isLoading" class="spinner"></span>
          <i v-else class="fas fa-key"></i>
          {{ isLoading ? 'Generating...' : 'Generate' }}
        </button>
      </div>
    </div>

    <!-- Display Codes Step -->
    <div v-if="step === 'display'" class="display-step">
      <div class="success-icon">
        <i class="fas fa-check"></i>
      </div>
      <h5>Save Your Recovery Codes</h5>
      <p class="warning-text">
        <i class="fas fa-exclamation-triangle"></i>
        Store these codes securely. They won't be shown again!
      </p>

      <div class="codes-grid">
        <div
          v-for="(code, index) in recoveryCodes"
          :key="index"
          class="code-item"
          :class="{ copied: copiedIndex === index }"
          @click="copyCode(code, index)"
        >
          <span class="code-number">{{ index + 1 }}</span>
          <span class="code-value">{{ code }}</span>
          <span class="copy-status">
            <i v-if="copiedIndex === index" class="fas fa-check"></i>
            <i v-else class="fas fa-copy"></i>
          </span>
        </div>
      </div>

      <div class="code-actions">
        <button class="action-btn" @click="copyAllCodes">
          <i class="fas fa-copy"></i>
          Copy All
        </button>
        <button class="action-btn" @click="downloadCodes">
          <i class="fas fa-download"></i>
          Download
        </button>
        <button class="action-btn" @click="printCodes">
          <i class="fas fa-print"></i>
          Print
        </button>
      </div>

      <div class="done-section">
        <label class="confirm-checkbox">
          <input type="checkbox" v-model="savedConfirmation" />
          <span>I have saved these recovery codes</span>
        </label>
        <button
          class="btn-done"
          :disabled="!savedConfirmation"
          @click="emit('close')"
        >
          Done
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.recovery-codes {
  padding: 1.5rem;
}

/* Intro Step */
.intro-step {
  text-align: center;
}

.intro-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.intro-icon i {
  font-size: 2rem;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.intro-step h5 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.75rem;
}

.intro-step p {
  color: #6b7280;
  margin: 0 0 1.5rem;
  line-height: 1.6;
}

.existing-warning {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #fef3c7;
  color: #92400e;
  border-radius: 10px;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  text-align: left;
}

.features {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.feature {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #374151;
  font-size: 0.9rem;
}

.feature i {
  color: #10b981;
}

.btn-generate {
  width: 100%;
  padding: 1rem;
  background: #6366f1;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-generate:hover {
  background: #4f46e5;
}

/* Confirm Step */
.confirm-step {
  text-align: center;
}

.confirm-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(99, 102, 241, 0.1));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-icon i {
  font-size: 1.5rem;
  color: #3b82f6;
}

.confirm-step h5 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.5rem;
}

.confirm-step p {
  color: #6b7280;
  margin: 0 0 1.5rem;
}

.password-field {
  text-align: left;
  margin-bottom: 1.5rem;
}

.password-field label {
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.password-field input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 1rem;
  outline: none;
  transition: all 0.2s ease;
}

.password-field input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.confirm-actions {
  display: flex;
  gap: 1rem;
}

.btn-back {
  padding: 0.875rem 1.25rem;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.btn-back:hover {
  background: #e5e7eb;
}

.btn-confirm {
  flex: 1;
  padding: 0.875rem 1.25rem;
  background: #6366f1;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.btn-confirm:hover:not(:disabled) {
  background: #4f46e5;
}

.btn-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Display Step */
.display-step {
  text-align: center;
}

.success-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1rem;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-icon i {
  font-size: 1.5rem;
  color: white;
}

.display-step h5 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.75rem;
}

.warning-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #dc2626;
  font-weight: 500;
  margin: 0 0 1.5rem;
}

.codes-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.code-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.code-item:hover {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.05);
}

.code-item.copied {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.1);
}

.code-number {
  width: 24px;
  height: 24px;
  background: #e5e7eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
}

.code-value {
  flex: 1;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  text-align: left;
}

.copy-status {
  color: #9ca3af;
  font-size: 0.85rem;
}

.code-item.copied .copy-status {
  color: #10b981;
}

.code-actions {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.action-btn {
  padding: 0.625rem 1rem;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: #e5e7eb;
}

.done-section {
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.confirm-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  cursor: pointer;
  color: #374151;
}

.confirm-checkbox input {
  width: 20px;
  height: 20px;
  accent-color: #6366f1;
}

.btn-done {
  width: 100%;
  padding: 1rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-done:hover:not(:disabled) {
  background: #059669;
}

.btn-done:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Dark mode support */
.dark-version .intro-step h5,
.dark-version .confirm-step h5,
.dark-version .display-step h5 {
  color: #f3f4f6;
}

.dark-version .intro-step p,
.dark-version .confirm-step p {
  color: #9ca3af;
}

.dark-version .feature,
.dark-version .confirm-checkbox {
  color: #d1d5db;
}

.dark-version .password-field label {
  color: #d1d5db;
}

.dark-version .password-field input {
  background: #1f2937;
  border-color: #374151;
  color: #f3f4f6;
}

.dark-version .password-field input:focus {
  border-color: #6366f1;
}

.dark-version .code-item {
  background: #1f2937;
  border-color: #374151;
}

.dark-version .code-value {
  color: #f3f4f6;
}

.dark-version .btn-back,
.dark-version .action-btn {
  background: #374151;
  color: #d1d5db;
}

.dark-version .btn-back:hover,
.dark-version .action-btn:hover {
  background: #4b5563;
}

.dark-version .done-section {
  border-color: #374151;
}

.dark-version .existing-warning {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}
</style>
